import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { SelectField, SelectFieldProps } from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { AccountChargeablePreauthorization } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { useGetChargeablePreauthorizationsSuspense } from '../forms/ProcessPayment/queries';
import { GatewayPaymentFormField, GatewayPaymentFormValues } from '../forms/ProcessPayment/GatewayPaymentForm/schema';
import { ProcessPaymentFormField } from '../forms/ProcessPayment/ProcessPaymentFormSchema';
import { useDefaultFormContext } from '../forms/ProcessPayment/useDefaultFormValues';
import { useConvertAmountToDefaultCurrencySuspense } from '../../hooks/useConvertAmountToDefaultCurrencySuspense';

interface PreauthorizationSelectFieldProps extends Omit<SelectFieldProps, 'options'> {
    accountId: string;
    isGateway: boolean;
}

const getDefaultPreauthorization = (
    data: AccountChargeablePreauthorization[],
    convertAmountToDefaultCurrency: (amount: number, currency: string) => number | undefined,
    billGrossAmount?: number
) => {
    const convertedPreauthorizations = data.map((preauthorization) => {
        const convertedAmount =
            convertAmountToDefaultCurrency(preauthorization.amount.value, preauthorization.amount.currencyCode) ?? 0;
        return { ...preauthorization, amount: { ...preauthorization.amount, value: convertedAmount } };
    });
    const sortedPreauthorizations = convertedPreauthorizations.sort((a, b) => a.amount.value - b.amount.value);
    const eligiblePreauthorizations = billGrossAmount
        ? sortedPreauthorizations.filter((preauthorization) => preauthorization.amount.value >= billGrossAmount)
        : sortedPreauthorizations;

    return eligiblePreauthorizations.length > 0
        ? eligiblePreauthorizations[0].id
        : sortedPreauthorizations[sortedPreauthorizations.length - 1]?.id || '';
};

const getDifferentCurrencyPreauthorizationLabel = (
    preauthorization: AccountChargeablePreauthorization,
    defaultCurrencyCode: string,
    convertAmountToDefaultCurrency: (amount: number, currency: string) => number | undefined
) => {
    const convertedAmount = convertAmountToDefaultCurrency(
        preauthorization.amount.value,
        preauthorization.amount.currencyCode
    );
    return `${preauthorization.label} ${UnicodeEntity.ArrowRight} ${
        UnicodeEntity.LeftParenthesis
    }${localizeCurrencyValue({
        value: convertedAmount ?? 0,
        currency: defaultCurrencyCode,
    })}${UnicodeEntity.RightParenthesis} `;
};

export const PreauthorizationSelectField = ({ accountId, isGateway, ...other }: PreauthorizationSelectFieldProps) => {
    const { defaultCurrencyCode } = useCurrentEnterprise();
    const { setValue, unregister, clearErrors } = useFormContext<GatewayPaymentFormValues>();
    const { data } = useGetChargeablePreauthorizationsSuspense({ accountId, isGateway });
    const {
        billGrossAmount,
        setSelectedCurrencyCode,
        defaultFormValues: { paymentMethodId },
    } = useDefaultFormContext();
    const convertAmountToDefaultCurrency = useConvertAmountToDefaultCurrencySuspense();

    const filteredPreauthorizationsByPaymentMethod = paymentMethodId
        ? data.filter((preauthorization) => preauthorization.paymentCard?.id === paymentMethodId)
        : data;

    const defaultPreauthorization = useMemo(
        () =>
            getDefaultPreauthorization(
                filteredPreauthorizationsByPaymentMethod,
                convertAmountToDefaultCurrency,
                billGrossAmount
            ),
        [filteredPreauthorizationsByPaymentMethod, billGrossAmount, convertAmountToDefaultCurrency]
    );

    const handlePreauthorizationChange = (preauthorizationId: string) => {
        const amount = filteredPreauthorizationsByPaymentMethod.find(
            (preauthorization) => preauthorization.id === preauthorizationId
        )?.amount;
        if (!amount || !billGrossAmount) {
            return;
        }
        const convertedAmount = convertAmountToDefaultCurrency(amount.value, amount.currencyCode) ?? 0;
        const updatedAmount = convertedAmount > billGrossAmount ? billGrossAmount : convertedAmount;
        setValue(ProcessPaymentFormField.CurrencyCode, amount.currencyCode);
        setValue(ProcessPaymentFormField.Amount, updatedAmount);
        setSelectedCurrencyCode(amount.currencyCode);
    };

    const options = filteredPreauthorizationsByPaymentMethod.map((preauthorization) => {
        let labelWithAmount: string;
        if (preauthorization.amount.currencyCode !== defaultCurrencyCode) {
            labelWithAmount = getDifferentCurrencyPreauthorizationLabel(
                preauthorization,
                defaultCurrencyCode,
                convertAmountToDefaultCurrency
            );
        } else {
            labelWithAmount = `${preauthorization.label}`;
        }
        return createOption({
            value: preauthorization.id ?? '',
            label: labelWithAmount,
        });
    });

    useEffect(() => {
        if (defaultPreauthorization) {
            setValue(GatewayPaymentFormField.PreAuthorizationId, defaultPreauthorization);
        }

        return () => {
            unregister(GatewayPaymentFormField.PreAuthorizationId);
            clearErrors(ProcessPaymentFormField.Amount);
        };
    }, [defaultPreauthorization, setValue, unregister, clearErrors]);

    return (
        <SelectField
            {...other}
            options={options}
            showEmptyValue={false}
            onChange={(_, value) => {
                handlePreauthorizationChange(value as string);
            }}
        />
    );
};
